import { useState, forwardRef, useImperativeHandle } from "react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// 定义组件属性类型
interface ConfirmDialogProps {
    title: string;
    description: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => void;
    onCancel?: () => void;
    open?: boolean; // 新增：支持外部控制显示状态
}

// 定义ref可调用的方法类型
interface ConfirmDialogRef {
    open: () => void;
}

// 使用forwardRef转发ref，使用useImperativeHandle定义暴露的方法
export const ConfirmDialog = forwardRef<ConfirmDialogRef, ConfirmDialogProps>(
    ({
         title,
         description,
         confirmText = "确认",
         cancelText = "取消",
         onConfirm,
         onCancel,
         open: externalOpen, // 重命名避免冲突
     }, ref) => {
        // 控制弹窗显示状态
        const [isOpen, setIsOpen] = useState(false);

        // 使用外部传入的 open 状态或内部状态
        const finalOpen = externalOpen !== undefined ? externalOpen : isOpen;

        // 打开弹窗方法
        const open = () => {
            setIsOpen(true);
        };

        // 关闭弹窗方法
        const close = () => {
            setIsOpen(false);
        };

        // 处理确认操作
        const handleConfirm = () => {
            onConfirm();
            if (externalOpen === undefined) {
                close(); // 只有内部控制时才自动关闭
            }
        };

        // 处理取消操作
        const handleCancel = () => {
            onCancel?.();
            if (externalOpen === undefined) {
                close(); // 只有内部控制时才自动关闭
            }
        };

        // 向外部暴露open方法
        useImperativeHandle(ref, () => ({
            open,
        }));

        return (
            <AlertDialog open={finalOpen} onOpenChange={externalOpen === undefined ? setIsOpen : undefined}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{title}</AlertDialogTitle>
                        <div style={{'wordBreak': 'break-all'}}>
                            <AlertDialogDescription>
                                {description}
                            </AlertDialogDescription>
                        </div>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={handleCancel}>
                            {cancelText}
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={handleConfirm}>
                            {confirmText}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        );
    }
);

// 设置组件显示名称（方便调试）
ConfirmDialog.displayName = "ConfirmDialog";